import React from 'react';
import {ReactFlowProvider} from 'reactflow';

import {DEFAULT_ENTITY_MAP_LAYOUT_SETTINGS} from 'registry/entity-map/EntityMapConstants';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {EntityMapMachineProvider, useEntityMapSelector} from './EntityMapMachineProvider';
import EntityMapView from './EntityMapView';

/**
 * Top-level component for displaying an entity map graph for a given entityId
 *
 * This component is responsible for providing the XState machine context to all descendants using
 * EntityMapMachineProvider. The entityId is passed as a prop to the EntityMapContent component.
 *
 * @param entityId  The ID of the root entity for the map.
 */
export default function EntityMap({entityId}: {entityId: string}): JSX.Element {
  return (
    <EntityMapMachineProvider entityId={entityId} layoutSettings={DEFAULT_ENTITY_MAP_LAYOUT_SETTINGS}>
      <EntityMapContent />
    </EntityMapMachineProvider>
  );
}

/**
 * Component for handling loading/error UI and delegating the actual rendering of the entity map to EntityMapView
 */
export function EntityMapContent(): JSX.Element {
  const isLoading = useEntityMapSelector((s) => s.matches('loading'));
  const isError = useEntityMapSelector((s) => s.matches('error'));

  if (isLoading) {
    return <CenteredHorizontalThrobber />;
  }
  if (isError) {
    return <div>There was an error in loading the entity map.</div>;
  }
  return (
    <div style={{height: '100%', width: '100%'}}>
      <ReactFlowProvider>
        <EntityMapView />
      </ReactFlowProvider>
    </div>
  );
}
